import type { CaptionPage, CaptionWord } from "./captions";

/**
 * Keywords in captions ("Schlüsselwörter hervorheben"): emphasised words keep
 * their accent for the whole caption, independent of the word being spoken.
 * The flag is stored per transcript word (`overrides[key].emphasis`, source
 * time), so it survives cuts and a new grouping into captions.
 *
 * The automatic pick is a local heuristic, at most one word per caption:
 * 1. a number, amount or percentage ("30 %", "4,99 €", "2026")
 * 2. a word of the keyword list (brand and product names)
 * 3. otherwise the longest content word (no German/English stop word)
 */

/** Common German and English function words that are never keywords. */
export const STOP_WORDS: ReadonlySet<string> = new Set(
  // German
  (
    "aber alle allem allen aller alles als also am an ander andere anderen auch auf aus bei beim " +
    "bin bis bist da dabei damit dann das dass dein deine dem den denn der des deshalb dich die " +
    "dies diese diesem diesen dieser dieses dir doch dort du durch ein eine einem einen einer " +
    "eines einfach er es etwas euch euer eure für gar gegen gibt habe haben hast hat hatte hier " +
    "hin hinter ich ihm ihn ihnen ihr ihre im immer in ins ist ja jede jedem jeden jeder jedes " +
    "jetzt kann kannst kein keine können könnt machen macht man mehr mein meine mich mir mit " +
    "muss müssen nach nicht nichts noch nun nur ob oder ohne schon sehr sein seine sich sie " +
    "sind so sogar solche soll sollte sondern sonst über um und uns unser unsere unter viel " +
    "vom von vor war waren warum was weil welche wenn wer werde werden wie wieder wir wird wirklich " +
    "wo wollen während zu zum zur zwischen eigentlich natürlich genau okay also halt mal gerade " +
    "heute dann denn deswegen darum dafür dazu daran darauf worden wurde wurden weiter " +
    // English
    "a about after again all also am an and any are as at be because been before being but by " +
    "can could did do does doing down during each few for from further had has have having he " +
    "her here hers him his how i if in into is it its just like me more most my no nor not now " +
    "of off on once only or other our out over own really same she should so some such than " +
    "that the their them then there these they this those through to too under until up very " +
    "was we were what when where which while who why will with would you your yours actually " +
    "going gonna thing things something everything"
  )
    .split(/\s+/)
    .filter(Boolean),
);

/** Word without surrounding punctuation, lower case. */
export function keywordToken(text: string): string {
  return text
    .toLocaleLowerCase("de-DE")
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
    .trim();
}

/** Numbers, amounts and percentages: "30", "30%", "4,99€", "1.000", "$5", "2x". */
export function isNumberWord(text: string): boolean {
  return (
    /\d/.test(text) && /^[^\p{L}]*[\d.,:]+\s*(?:%|€|\$|£|x|k|mio\.?|mrd\.?)?[^\p{L}]*$/iu.test(text)
  );
}

function contentLength(word: CaptionWord): number {
  const token = keywordToken(word.text);
  if (token.length < 4 || STOP_WORDS.has(token)) return 0;
  return [...token].length;
}

/** Tokens of the keyword list; names with several words count word by word. */
export function keywordSet(keywords: readonly string[]): Set<string> {
  return new Set(
    keywords
      .flatMap((keyword) => keyword.split(/\s+/))
      .map(keywordToken)
      .filter(Boolean),
  );
}

/** The keyword of one caption, or null when it only has stop words. */
export function pickPageKeyword(
  words: readonly CaptionWord[],
  keywords: ReadonlySet<string>,
): CaptionWord | null {
  const number = words.find((word) => isNumberWord(word.text));
  if (number) return number;
  const listed = words.find((word) => keywords.has(keywordToken(word.text)));
  if (listed) return listed;
  let best: CaptionWord | null = null;
  let bestLength = 0;
  for (const word of words) {
    const length = contentLength(word);
    if (length > bestLength) {
      best = word;
      bestLength = length;
    }
  }
  return best;
}

/**
 * Caption-word keys of the automatic keywords, at most one per caption.
 * Pages that already have an emphasised word are left alone.
 */
export function autoEmphasisKeys(
  pages: readonly CaptionPage[],
  keywords: readonly string[] = [],
): string[] {
  const list = keywordSet(keywords);
  const out: string[] = [];
  for (const page of pages) {
    const words = page.lines.flat();
    if (words.some((word) => word.emphasis)) continue;
    const pick = pickPageKeyword(words, list);
    if (pick) out.push(pick.key);
  }
  return out;
}

/**
 * Key of the transcript word a caption word belongs to: a correction with
 * several words is shown as `key:index`, its emphasis is stored on `key`.
 */
export function transcriptWordKey(captionKey: string, transcriptKeys: ReadonlySet<string>): string {
  if (transcriptKeys.has(captionKey)) return captionKey;
  const base = captionKey.replace(/:\d+$/, "");
  return transcriptKeys.has(base) ? base : captionKey;
}
